import { Collapsible, CollapsibleContent, CollapsibleTrigger, Input, cn } from "@tellescope/ui";
import { ChevronDown } from "lucide-react";
import { Ref, useImperativeHandle } from "react";
import { useTranslation } from "react-i18next";

import { TokenBadge } from "./TokenBadge";
import { TokenCollectorPopover } from "./TokenCollectorPopover";
import { BeginDropCollectionArgs, useTokenPalette } from "./useTokenPalette";

/** Imperative surface for the editor to open the collector from a drop at the drop point. */
export interface TokenPaletteHandle {
  beginDropCollection: (args: BeginDropCollectionArgs) => void;
}

export interface TokenPaletteProps {
  onInsert: (raw: string) => void;
  /** Attached to the search input so the parent can focus it when the panel opens. */
  searchRef?: Ref<HTMLInputElement>;
  /** Exposes {@link TokenPaletteHandle} so the editor's drop handler can open the collector. */
  handleRef?: Ref<TokenPaletteHandle>;
}

/**
 * The side panel of insertable tokens: a label search over the full catalog, grouped by namespace
 * into collapsible sections (collapsed by default, auto-expanded while searching), with
 * draggable/clickable badges. Parameterized tokens open the generic collector in an anchored popover
 * (never a stacked dialog), positioned at the clicked badge or the drop point.
 */
export const TokenPalette = ({ onInsert, searchRef, handleRef }: TokenPaletteProps) => {
  const { t } = useTranslation("templates");
  const {
    search,
    setSearch,
    groups,
    isGroupOpen,
    setGroupOpen,
    activeCollector,
    collectorAnchor,
    selectToken,
    beginDropCollection,
    submitCollector,
    closeCollector,
  } = useTokenPalette(onInsert);

  useImperativeHandle(handleRef, () => ({ beginDropCollection }), [beginDropCollection]);

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-2">
      <Input
        ref={searchRef}
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        placeholder={t("Search template fields...")}
      />
      <div className="flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto pr-1">
        {groups.map((group) => {
          const open = isGroupOpen(group.id);
          return (
            <Collapsible key={group.id} open={open} onOpenChange={(next) => setGroupOpen(group.id, next)}>
              <CollapsibleTrigger className="flex w-full items-center justify-between rounded px-1 py-1 text-xs font-medium uppercase tracking-wide text-muted-foreground hover:bg-muted">
                <span>{group.label}</span>
                <ChevronDown className={cn("size-4 transition-transform", open && "rotate-180")} />
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="flex flex-wrap gap-1 px-1 pb-2 pt-1">
                  {group.tokens.map((definition) => (
                    <TokenBadge key={definition.id} definition={definition} onSelect={selectToken} />
                  ))}
                </div>
              </CollapsibleContent>
            </Collapsible>
          );
        })}
      </div>

      <TokenCollectorPopover
        definition={activeCollector}
        anchor={collectorAnchor}
        onSubmit={submitCollector}
        onClose={closeCollector}
      />
    </div>
  );
};
